import _ from "lodash";

import { descendants } from "../canvas/store";
import { mapPath } from "../reusable/utils";

const cloneId = (id, idx) => id + "_" + (idx + 1);

const mergeFromArray = arr => arr.reduce((acc, c) => ({ ...acc, ...c }), {});

const cloneBlock = (block, data, idx, childDelay) => ({
  [cloneId(block.id, idx)]: {
    ...linkProps(block, data),
    cloneKey: cloneId(block.id, idx),
    children: block.children.map(c => cloneId(c, idx)),
    delay: childDelay ? block.delay + (idx + 1) * childDelay : block.delay
  }
});

const cloneFromCollection = (block, rows, collection, childDelay) => {
  const clones = rows.map((row, idx) => cloneBlock(block, row, idx, childDelay));
  return mergeFromArray(clones);
};

const linkAll = (blocks, block, rows) => {
  const linked = descendants(blocks, block.id)
    .concat([block.id])
    .map(id => {
      return {
        ...cloneFromCollection(blocks[id], _.tail(rows), block.collection, block.childDelay),
        [id]: {
          ...linkProps(blocks[id], rows[0])
        }
      };
    });
  return mergeFromArray(linked);
};

const strafeBlocks = (blocks, block, items) => {
  const ids = items.map((i, idx) => cloneId(block.id, idx));
  const strafe = (axis, size, dir, idx) =>
    block.direction === dir ? block[axis] + (block[size] + block.space) * (idx + 1) : block[axis];
  return ids.reduce(
    (acc, id, idx) => ({
      ...acc,
      [id]: {
        ...acc[id],
        top: strafe("top", "height", "vertical", idx),
        left: strafe("left", "width", "horizontal", idx)
      }
    }),
    blocks
  );
};

const linkCollection = (blocks, items, block) => {
  const ids = _.tail(items).map((i, idx) => cloneId(block.id, idx));
  const withParent = mapPath([block.parent, "children"], c => c.concat(ids), blocks);

  const linked = linkAll(blocks, block, items);
  const strafed = strafeBlocks(linked, block, _.tail(items));

  return {
    ...withParent,
    ...strafed
  };
};

export const linkCollections = (blocks, flatten) => {
  return _.reduce(
    blocks,
    (acc, v, k) => {
      if (v.collection && _.get(flatten, v.collection)) {
        const coll = _.chain(flatten)
          .get(v.collection)
          .map((i, idx) => ({ ...i, [v.collection + "._position"]: idx + 1 }))
          .drop(v.cycle ? v._skip % _.get(flatten, v.collection).length : v.skip)
          .take(v.limit)
          .value();
        return linkCollection(acc, coll, v);
      } else {
        return acc;
      }
    },
    blocks
  );
};

export const resolveCollection = (blocks, flatten, blockId) => {
  const block = blocks[blockId];

  const newC = block.children ? block.children.map(b => resolveCollection(blocks, flatten, b)) : [];
  const added = _.flatten(newC);
  const withParent = added.concat([
    {
      ...block,
      children: added.filter(c => c.parent === blockId).map(c => c.cloneKey || c.id)
    }
  ]);
  const coll = block.collection;
  if (coll && _.get(flatten, coll)) {
    const items = _.chain(flatten)
      .get(block.collection)
      .map((i, idx) => ({ ...i, [block.collection + "._position"]: idx + 1 }))
      .drop(block.cycle ? block._skip % _.get(flatten, block.collection).length : block.skip)
      .take(block.limit)
      .value();
    return makeClones(arrayToBlocks(withParent, blocks), items, blockId);
  } else return withParent;
};

const arrayToBlocks = (arr, bs) =>
  arr.reduce((acc, c) => ({ ...acc, [c.cloneKey || c.id]: c }), bs);

//nested colls not working because the recursion is resolving the tree leafs before the parent
//so there is no way to know where to find the nested collection.
//items in the coll need to be passed down by the parent.
export const resolveCollections = (blocks, flatten, blockId) => {
  return arrayToBlocks(resolveCollection(blocks, flatten, blockId), blocks);
};

const strafe = (block, axis, size, dir, idx) =>
  block.direction === dir ? block[axis] + (block[size] + block.space) * idx : block[axis];

export const makeClones = (blocks, items, blockId) => {
  return _.flatten(
    descendants(blocks, blockId)
      .concat([blockId])
      .map(id => {
        const b = blocks[id];
        return items.map((i, idx) => ({
          ...linkProps(b, i),
          cloneKey: cloneId(id, idx),
          children: b.children.map(c => cloneId(c, idx)),
          fixed: idx > 0,
          top: blockId === id ? strafe(b, "top", "height", "vertical", idx) : b.top,
          left: blockId === id ? strafe(b, "left", "width", "horizontal", idx) : b.left
        }));
      })
  );
};

// export const testColl = () =>
//   resolveCollections(
//     {
//       canvas: { children: ["text", "image", "container"], id: "canvas" },
//       text: { id: "text", children: [], collection: "items", parent: "canvas" },
//       container: { id: "container", children: ["triangle"], collection: "items", parent: "canvas" },
//       triangle: { id: "triangle", children: [], parent: "container", collection: "coll" },
//       image: { id: "image", children: [], parent: "canvas" }
//     },
//     { items: ["item1", "item2"], coll: ["coll1", "coll2"] },
//     "canvas"
//   );

export const processTemplate = (tmpl, data) => {
  return typeof tmpl === "string"
    ? _.reduce(data, (res, v, k) => res.replace("{" + k + "}", v), tmpl)
    : tmpl;
};

export const linkProps = (blockProps, data) => {
  return _.mapValues(blockProps, (v, k) => {
    return v && v.target
      ? v.multiplier ? data[v.target] * v.multiplier : data[v.target]
      : processTemplate(v, data);
  });
};

export const RuleComparator = {
  Equal: "equal",
  Greater: "greater",
  Less: "less",
  Between: "between"
};

const comparatorFn = c => {
  return {
    [RuleComparator.Equal]: (a, b) => a === b,
    [RuleComparator.Greater]: (a, b) => a > b,
    [RuleComparator.Less]: (a, b) => a < b,
    [RuleComparator.Between]: (a, b) => a >= b[0] && a <= b[1]
  }[c];
};

const resolveRule = (blockProps, data) => {
  return blockProps.rules.reduce((block, r) => {
    const doesApply = comparatorFn(r.comparator)(data[r.cell], r.constant);
    const update = doesApply && { [r.property]: r.value };
    return { ...block, ...update };
  }, blockProps);
};

export const resolveRules = (blocks, data) =>
  _.mapValues(blocks, v => (v.rules ? resolveRule(v, data) : v));



// WEBPACK FOOTER //
// ./src/spreadsheet/linker.js